import { arrayMove } from 'react-sortable-hoc';
import { cloneDeep } from 'lodash';
import type { Reducer, Effect, Subscription, Dispatch } from 'umi';

import { formatList } from '../_data.d';
import { fetchLatestHomepageConfig } from '../service';
import { handleLinkUrlType } from '../common/LinkComponent';
import { BgImageType } from '../componentList/TopNavbar/data.d';
import pageConfigDefaultValue from '../componentList/TopNavbar/defaultValue';

/** 定制化配置数据
 * @param format          页面版式
 * @param formatList      页面版式列表
 * @param configType      配置元素类型，page（默认）、component
 * @param pageConfig      页面配置
 * @param currentIndex    当前组件序号
 * @param componentList   组件列表
 * @param originData      页面原始数据
 */
export type CustomizationState = {
  format: number;
  formatList: Record<string, any>[];
  configType: 'page' | 'component';
  pageConfig: Record<string, any>;
  currentIndex: number;
  componentList: Record<string, any>[];
  originData: Record<string, any>;
}
export type CustomizationStateProps = {
  homepage: CustomizationState;
  dispatch: Dispatch;
}
export const mapToPropsOfHomepageDecoration = ({ homepage }: { homepage: CustomizationState }) => {
  return {
    homepage
  }
}
export type CustomizationType = {
  namespace: 'homepage';
  state: CustomizationState,
  reducers: Record<string, Reducer>;
  effects: Record<string, Effect>;
  subscriptions: Record<string, Subscription>;
};


const HomepageModel: CustomizationType = {
  namespace: 'homepage',
  state: {
    format: 1,
    formatList: formatList,
    configType: 'page',
    pageConfig: pageConfigDefaultValue,
    currentIndex: 0,
    componentList: [],
    originData: {},
  },
  reducers: {
    // 页面版式相关设置
    saveOriginData(state, { payload }) { // 保存原始数据，用于重置
      state.originData = payload;
    },
    updateFormat(state, { payload }) {
      state.format = payload;
    },
    switchConfigType(state, { payload }) {
      state.configType = payload;
    },
    updatePageConfig(state, { payload }) {
      state.pageConfig = payload;
    },

    // 组件相关设置
    updateComponentList(state, { payload }) {
      state.componentList = payload;
    },
    setCurrentIndex(state, { payload }) {
      state.currentIndex = payload;
    },
  },
  effects: {
    // 页面版式相关设置
    *getLatestHomepageData(action, { call, put }) { // 调用接口获取最近一次首页配置，保存原始数据并更新首页数据
      try {
        const { code, data } = yield call(fetchLatestHomepageConfig);
        if (code === 200) {
          // 修复 bug3237，跳转链接类型问题，系统预设中 tab
          data.componentList = data?.componentList?.map((component: Record<string, any>) => {
            const { componentName } = component;
            if (componentName === 'navigation_buttons') { // 导航按钮，数据 data: { bigButtons, smallButtons, cardList }
              component.data.bigButtons = handleLinkUrlType(1, component?.data?.bigButtons);
              component.data.smallButtons = handleLinkUrlType(1, component?.data?.smallButtons);
              component.data.cardList = handleLinkUrlType(1, component?.data?.cardList);
            }
            if (componentName === 'slide_show') { // 轮播图，数据 data: { detail }
              component.data.detail = handleLinkUrlType(1, component?.data?.detail);
            }
            if (componentName === 'img') { // 图片组件，数据 data: { detail }
              component.data.detail = handleLinkUrlType(1, component?.data?.detail);
            }
            return component;
          }) || [];
          yield put({ type: 'saveOriginData', payload: cloneDeep(data) });
          yield put({ type: 'updateHomepageData', payload: data });
          yield put({ type: 'switchConfigType', payload: 'page' });
        }
      } catch (err) {
        console.log(err);
      }
    },
    *updateHomepageData({ payload }, { put }) { // 更新配置，用于初始化数据、重置数据
      yield put({ type: 'updateFormat', payload: payload?.format });
      const {
        tenantId, id, status, language,
        headColor, title,
        // paddingTop, paddingBottom,
      } = payload || {};
      const backgroundColor = payload?.backgroundColor || '';
      // 解析 img => { backgroundImageType, img, backgroundSize, backgroundRepeat }
      let backgroundImageType, img, backgroundSize, backgroundRepeat;
      try {
        const tempData = JSON.parse(payload?.img || '{}');
        backgroundImageType = tempData?.backgroundImageType || BgImageType.背景渐变色;
        img                 = tempData?.img;
        backgroundSize      = tempData?.backgroundSize;
        backgroundRepeat    = tempData?.backgroundRepeat;
      } catch(err) {
        backgroundImageType = BgImageType.背景渐变色;
      }
      const showTitle = payload?.showTitle > 0 ? 1 : 0;
      // 解析 color => { bgColor, scrollBgColor }
      let color = { bgColor: '', scrollBgColor: '' };
      try {
        color = JSON.parse(payload?.color || '{}');
      } catch (err) {
        console.log(err);
      }
      // 解析 fontColor => { color, scrollColor }
      let fontColor = { color: '', scrollColor: '' };
      try {
        fontColor = JSON.parse(payload?.fontColor || '{}');
      } catch (err) {
        console.log(err);
      }
      yield put({
        type: 'updatePageConfig',
        payload: {
          tenantId, id, status, language,
          backgroundColor, headColor, img,
          backgroundImageType, backgroundSize, backgroundRepeat,
          color, showTitle, title, fontColor,
          // paddingTop, paddingBottom,
        }
      });
      yield put({ type: 'updateComponentList', payload: payload?.componentList || [] });
    },
    *resetHomepageData(action, { put, select }) {
      const { originData } = yield select((state: any) => state.homepage);
      yield put({ type: 'updateHomepageData', payload: originData });
      yield put({ type: 'switchConfigType', payload: 'page' });
    },

    *addComponent({ payload }, { put, select }) { // 添加新组件到末尾
      const componentList: Record<string, any>[] = yield select((state: any) => state.homepage.componentList);
      yield put({ type: 'updateComponentList', payload: [...componentList, payload] });
      yield put({ type: 'switchConfigType', payload: 'component' });
      yield put({ type: 'setCurrentIndex', payload: [...componentList].length });
    },
    *sortComponents({ payload: { oldIndex, newIndex } }, { put, select }) { // 组件重新排序
      const { componentList } = yield select((state: any) => state.homepage);
      yield put({ type: 'updateComponentList', payload: arrayMove([...componentList], oldIndex, newIndex) });
      yield put({ type: 'setCurrentIndex', payload: newIndex });
    },
    *editComponent({ payload }, { put }) { // 开始编辑组件信息，设置 configType、currentIndex
      yield put({ type: 'switchConfigType', payload: 'component' });
      yield put({ type: 'setCurrentIndex', payload });
    },
    *updateComponentData({ payload }, { select, put }) { // 更新组件数据
      const { currentIndex, componentList } = yield select((state: any) => state.homepage);
      const tempComponentList = componentList.map((item: Record<string, any>, index: number) => {
        if (index !== currentIndex) return { ...item };
        return { ...item, data: payload };
      });
      yield put({ type: 'updateComponentList', payload: tempComponentList });
    },
    *deleteComponent({ payload }, { put, select }) { // 删除组件，将组件从 componentList 中删除
      const componentList: Record<string, any>[] = yield select((state: any) => state.homepage.componentList);
      yield put({ type: 'updateComponentList', payload: componentList.filter((_, index) => index !== payload )});
      // yield put({ type: 'setCurrentIndex', payload: 0 });
    },
  },
  subscriptions: {
    setup({ history, dispatch }) {
      return history.listen(({ pathname }) => {
        if (pathname === '/TenantSettings/HomepageDecoration') {
          dispatch({ type: 'getLatestHomepageData' });
        }
      });
    },
  }
}

export default HomepageModel;
